import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CropArea,
  ResizeHandle,
  ImageNaturalSize,
  DragState,
} from './types';

interface UseCropInteractionProps {
  imageRef: React.RefObject<HTMLImageElement | null>;
  imageNaturalSize: ImageNaturalSize;
  aspectRatio?: number;
  minWidth: number;
  minHeight: number;
  onCropAreaChange: (area: CropArea) => void;
  cropArea: CropArea;
}

interface UseCropInteractionReturn {
  isDragging: boolean;
  isResizing: ResizeHandle | null;
  handleMouseDown: (e: React.MouseEvent, handle?: ResizeHandle) => void;
  handleTouchStart: (e: React.TouchEvent, handle?: ResizeHandle) => void;
}

// Calculate new crop area based on resize handle and delta
function calculateResizeArea(
  handle: ResizeHandle,
  prev: CropArea,
  deltaX: number,
  deltaY: number,
  minWidth: number,
  minHeight: number
): CropArea {
  const newArea = { ...prev };

  switch (handle) {
    case 'nw': {
      const maxDeltaX = prev.width - minWidth;
      const maxDeltaY = prev.height - minHeight;
      const actualDeltaX = Math.min(deltaX, maxDeltaX);
      const actualDeltaY = Math.min(deltaY, maxDeltaY);
      newArea.x += actualDeltaX;
      newArea.y += actualDeltaY;
      newArea.width -= actualDeltaX;
      newArea.height -= actualDeltaY;
      break;
    }
    case 'ne': {
      const maxDeltaY = prev.height - minHeight;
      const actualDeltaY = Math.min(deltaY, maxDeltaY);
      newArea.y += actualDeltaY;
      newArea.width += deltaX;
      newArea.height -= actualDeltaY;
      break;
    }
    case 'sw': {
      const maxDeltaX = prev.width - minWidth;
      const actualDeltaX = Math.min(deltaX, maxDeltaX);
      newArea.x += actualDeltaX;
      newArea.width -= actualDeltaX;
      newArea.height += deltaY;
      break;
    }
    case 'se':
      newArea.width += deltaX;
      newArea.height += deltaY;
      break;
    case 'n': {
      const maxDeltaY = prev.height - minHeight;
      const actualDeltaY = Math.min(deltaY, maxDeltaY);
      newArea.y += actualDeltaY;
      newArea.height -= actualDeltaY;
      break;
    }
    case 's':
      newArea.height += deltaY;
      break;
    case 'w': {
      const maxDeltaX = prev.width - minWidth;
      const actualDeltaX = Math.min(deltaX, maxDeltaX);
      newArea.x += actualDeltaX;
      newArea.width -= actualDeltaX;
      break;
    }
    case 'e':
      newArea.width += deltaX;
      break;
  }

  return newArea;
}

export function useCropInteraction({
  imageRef,
  imageNaturalSize,
  aspectRatio,
  minWidth,
  minHeight,
  onCropAreaChange,
  cropArea,
}: UseCropInteractionProps): UseCropInteractionReturn {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<ResizeHandle | null>(null);
  const dragStartRef = useRef<DragState>({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<{ deltaX: number; deltaY: number } | null>(
    null
  );
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef<ResizeHandle | null>(null);

  const constrainCropArea = useCallback(
    (area: CropArea): CropArea => {
      const constrained = { ...area };

      // First, clamp position to ensure we start within bounds
      constrained.x = Math.max(
        0,
        Math.min(constrained.x, imageNaturalSize.width)
      );
      constrained.y = Math.max(
        0,
        Math.min(constrained.y, imageNaturalSize.height)
      );

      // Ensure minimum size
      constrained.width = Math.max(constrained.width, minWidth);
      constrained.height = Math.max(constrained.height, minHeight);

      // Maintain aspect ratio if specified
      if (aspectRatio) {
        const widthChanged = Math.abs(area.width - constrained.width) > 0.1;
        const heightChanged = Math.abs(area.height - constrained.height) > 0.1;

        if (widthChanged && !heightChanged) {
          constrained.height = constrained.width / aspectRatio;
        } else if (heightChanged && !widthChanged) {
          constrained.width = constrained.height * aspectRatio;
        } else {
          constrained.height = constrained.width / aspectRatio;
        }
      }

      // Ensure crop area stays within image bounds
      if (constrained.x + constrained.width > imageNaturalSize.width) {
        constrained.width = imageNaturalSize.width - constrained.x;
        if (aspectRatio) {
          constrained.height = constrained.width / aspectRatio;
        }
      }
      if (constrained.y + constrained.height > imageNaturalSize.height) {
        constrained.height = imageNaturalSize.height - constrained.y;
        if (aspectRatio) {
          constrained.width = constrained.height * aspectRatio;
        }
      }

      // Final bounds check
      constrained.x = Math.max(
        0,
        Math.min(constrained.x, imageNaturalSize.width - constrained.width)
      );
      constrained.y = Math.max(
        0,
        Math.min(constrained.y, imageNaturalSize.height - constrained.height)
      );
      constrained.width = Math.min(
        constrained.width,
        imageNaturalSize.width - constrained.x
      );
      constrained.height = Math.min(
        constrained.height,
        imageNaturalSize.height - constrained.y
      );

      return constrained;
    },
    [aspectRatio, minWidth, minHeight, imageNaturalSize]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, handle?: ResizeHandle) => {
      e.preventDefault();
      e.stopPropagation();

      if (handle) {
        setIsResizing(handle);
        isResizingRef.current = handle;
      } else {
        setIsDragging(true);
        isDraggingRef.current = true;
      }

      dragStartRef.current = { x: e.clientX, y: e.clientY };
    },
    []
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent, handle?: ResizeHandle) => {
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      if (!touch) return;

      if (handle) {
        setIsResizing(handle);
        isResizingRef.current = handle;
      } else {
        setIsDragging(true);
        isDraggingRef.current = true;
      }

      dragStartRef.current = { x: touch.clientX, y: touch.clientY };
    },
    []
  );

  const processUpdate = useCallback(
    (clientX: number, clientY: number) => {
      const imageRect = imageRef.current?.getBoundingClientRect();
      if (!imageRect || !imageNaturalSize.width || !imageNaturalSize.height)
        return;

      const actualScaleX = imageRect.width / imageNaturalSize.width;
      const actualScaleY = imageRect.height / imageNaturalSize.height;

      const deltaX = (clientX - dragStartRef.current.x) / actualScaleX;
      const deltaY = (clientY - dragStartRef.current.y) / actualScaleY;

      pendingUpdateRef.current = { deltaX, deltaY };

      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = null;

          const pending = pendingUpdateRef.current;
          if (!pending) return;

          if (isDraggingRef.current) {
            onCropAreaChange(
              constrainCropArea({
                ...cropArea,
                x: cropArea.x + pending.deltaX,
                y: cropArea.y + pending.deltaY,
              })
            );
          } else if (isResizingRef.current) {
            const newArea = calculateResizeArea(
              isResizingRef.current,
              cropArea,
              pending.deltaX,
              pending.deltaY,
              minWidth,
              minHeight
            );
            onCropAreaChange(constrainCropArea(newArea));
          }

          pendingUpdateRef.current = null;
        });
      }

      dragStartRef.current = { x: clientX, y: clientY };
    },
    [
      constrainCropArea,
      cropArea,
      imageRef,
      imageNaturalSize,
      minHeight,
      minWidth,
      onCropAreaChange,
    ]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current && !isResizingRef.current) return;
      processUpdate(e.clientX, e.clientY);
    },
    [processUpdate]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDraggingRef.current && !isResizingRef.current) return;

      try {
        e.preventDefault();
      } catch {
        // Ignore preventDefault errors in passive listeners
      }

      const touch = e.touches[0];
      if (!touch) return;

      processUpdate(touch.clientX, touch.clientY);
    },
    [processUpdate]
  );

  const handleEnd = useCallback(() => {
    setIsDragging(false);
    setIsResizing(null);
    isDraggingRef.current = false;
    isResizingRef.current = null;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    pendingUpdateRef.current = null;
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleEnd);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        pendingUpdateRef.current = null;
      };
    }
    return undefined;
  }, [isDragging, isResizing, handleMouseMove, handleTouchMove, handleEnd]);

  return {
    isDragging,
    isResizing,
    handleMouseDown,
    handleTouchStart,
  };
}
